'use client';

import { Spinner } from '@/components/glitch-ui/spinner';
import { useRef, useState } from 'react';
import { Globe, Home, Package, Plus, Search, X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { addLandingPage } from '@/lib/apis';
import { useUrlValidation } from '@/hooks/useUrlValidation';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

export type LandingPageEntryType = 'homepage' | 'product' | 'custom';

export interface LandingPageEntry {
  id: string;
  label: string;
  url: string;
  type: LandingPageEntryType;
}

const ENTRY_ICONS: Record<LandingPageEntryType, React.ElementType> = {
  homepage: Home,
  product: Package,
  custom: Globe,
};

const URL_PATTERN = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+/i;

const ensureProtocol = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const normalizeForComparison = (url: string) =>
  url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

interface LandingPageComboboxProps {
  entries: LandingPageEntry[];
  businessId?: number;
  value?: LandingPageEntry | null;
  onSelect: (entry: LandingPageEntry | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Forwarded to the search input so a `<Label htmlFor>` can associate with it (#6182). */
  inputId?: string;
}

export function LandingPageCombobox({
  entries,
  businessId,
  value = null,
  onSelect,
  placeholder = 'Search or paste a URL...',
  disabled = false,
  inputId,
}: LandingPageComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { validateUrl, isFetching: isValidating, error: validationError } = useUrlValidation();
  const queryClient = useQueryClient();

  const looksLikeUrl = URL_PATTERN.test(query.trim());

  const filtered = query.trim()
    ? entries.filter(
        (e) =>
          e.label.toLowerCase().includes(query.toLowerCase()) ||
          normalizeForComparison(e.url).includes(normalizeForComparison(query)),
      )
    : entries;

  const exactMatch = entries.find(
    (e) => normalizeForComparison(e.url) === normalizeForComparison(query.trim()),
  );

  const showAddOption = looksLikeUrl && !exactMatch;

  const grouped = {
    homepage: filtered.filter((e) => e.type === 'homepage'),
    product: filtered.filter((e) => e.type === 'product'),
    custom: filtered.filter((e) => e.type === 'custom'),
  };

  const handleSelect = (entry: LandingPageEntry) => {
    onSelect(entry);
    setQuery('');
    setAddError(null);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
    setQuery('');
    setAddError(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleAdd = async () => {
    if (!query.trim()) return;
    setAddError(null);
    setIsAdding(true);
    try {
      // With a businessId the server-side validator normalizes; without one (the domain-
      // filtered Add Ad path saves manually upstream) still guarantee a scheme so a raw
      // "www.example.com" never reaches onSelect and the ad-create domain gate.
      const normalizedUrl = businessId ? await validateUrl(query.trim()) : ensureProtocol(query.trim());
      if (businessId) {
        await addLandingPage(businessId, normalizedUrl);
        queryClient.invalidateQueries({ queryKey: ['landingPagesDetails', businessId] });
      }
      const newEntry: LandingPageEntry = {
        id: `custom_${normalizedUrl}`,
        label: (() => {
          try {
            const u = new URL(normalizedUrl);
            const segments = u.pathname.split('/').filter(Boolean);
            const last = segments[segments.length - 1];
            return last
              ? last.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
              : u.hostname;
          } catch {
            return normalizedUrl;
          }
        })(),
        url: normalizedUrl,
        type: 'custom',
      };
      handleSelect(newEntry);
    } catch {
      setAddError(validationError || 'Failed to add landing page');
    } finally {
      setIsAdding(false);
    }
  };

  const busy = isAdding || isValidating;
  const hasResults =
    grouped.homepage.length > 0 || grouped.product.length > 0 || grouped.custom.length > 0 || showAddOption;

  const SelectedIcon = value ? ENTRY_ICONS[value.type] : null;

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setOpen(false);
          setQuery('');
          setAddError(null);
        }
      }}
    >
      <div
        className={cn(
          'flex items-center min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          (disabled || busy) && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => {
          if (!disabled && !busy && !value) {
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        {value ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex-shrink-0 w-6 h-6 rounded bg-gray-100 flex items-center justify-center">
              {SelectedIcon && <SelectedIcon className="h-3.5 w-3.5 text-gray-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-gray-900 block truncate">{value.label}</span>
              <span className="text-xs text-gray-500 block truncate">{value.url}</span>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 ml-1"
              tabIndex={0}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center flex-1 min-w-0 gap-2">
            {busy ? (
              <Spinner size="sm" className="text-gray-400 flex-shrink-0" />
            ) : (
              <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
            <input
              ref={inputRef}
              id={inputId}
              value={query}
              placeholder={placeholder}
              disabled={disabled || busy}
              className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              onChange={(e) => {
                setQuery(e.target.value);
                setAddError(null);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setQuery('');
                }
                if (e.key === 'Enter' && showAddOption && !busy) {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
          </div>
        )}
      </div>

      {addError && <p className="text-sm text-[#B91C1C] mt-1">{addError}</p>}

      {open && !value && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
          <Command shouldFilter={false}>
            <CommandList className="max-h-64">
              {!hasResults && (
                // Typed text that is neither an existing page nor URL-shaped lands here (a URL-shaped
                // query shows the Add option instead). Point the user at what actually works — pasting
                // a full address — rather than the dead-end "no results" (#6163 runthrough).
                <CommandEmpty className="px-3 py-4 text-center text-sm text-gray-500">
                  {query.trim()
                    ? 'No matching page. To add a new one, paste its full URL, like https://example.com/page.'
                    : 'No saved pages yet. Paste a full URL to add one.'}
                </CommandEmpty>
              )}

              {(grouped.homepage.length > 0 || grouped.product.length > 0 || grouped.custom.length > 0) && (
                <CommandGroup>
                  {grouped.homepage.map((entry) => (
                    <EntryItem key={entry.id} entry={entry} onSelect={handleSelect} />
                  ))}
                  {grouped.product.map((entry) => (
                    <EntryItem key={entry.id} entry={entry} onSelect={handleSelect} />
                  ))}
                  {grouped.custom.map((entry) => (
                    <EntryItem key={entry.id} entry={entry} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              )}

              {showAddOption && (
                <CommandGroup heading="Add New">
                  <CommandItem
                    value="__add__"
                    onSelect={handleAdd}
                    disabled={busy}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center bg-[#EDE9FE]">
                      <Plus className="h-3.5 w-3.5 text-[#290BD9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#290BD9] block">Add as landing page</span>
                      <span className="text-xs text-gray-500 truncate block">{query.trim()}</span>
                    </div>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

function EntryItem({
  entry,
  onSelect,
}: {
  entry: LandingPageEntry;
  onSelect: (e: LandingPageEntry) => void;
}) {
  const Icon = ENTRY_ICONS[entry.type];
  return (
    <CommandItem
      value={entry.id}
      onSelect={() => onSelect(entry)}
      className="flex items-center gap-2 cursor-pointer"
    >
      <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
        <Icon className="h-3.5 w-3.5 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 block truncate">{entry.label}</span>
        <span className="text-xs text-gray-500 truncate block">{entry.url}</span>
      </div>
    </CommandItem>
  );
}
